import { useState, useEffect, useCallback } from 'react';
import { useServices, useToast } from '@/app/providers';
import type { IncomeExpenseReport, IncomeExpenseParams, ExportFormat } from '@yawi/types';
import { downloadBlob, generateReportFilename } from '../utils/download';

export const useIncomeExpense = () => {
  const { reportRepo } = useServices();
  const toast = useToast();

  const currentYear = new Date().getFullYear();

  const [report, setReport] = useState<IncomeExpenseReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [params, setParams] = useState<IncomeExpenseParams>({
    year: currentYear,
  });

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await reportRepo.getIncomeExpenseReport(params);
      setReport(data);
    } catch (e) {
      console.error('Error loading income/expense report:', e);
      toast.error('Error al cargar el reporte de ingresos y egresos');
    } finally {
      setLoading(false);
    }
  }, [reportRepo, params, toast]);

  const exportReport = useCallback(async (format: ExportFormat) => {
    setExporting(true);
    try {
      const blob = await reportRepo.exportIncomeExpenseReport(format, params);
      const filename = generateReportFilename(`ingresos-egresos-${params.year}`, format);
      downloadBlob(blob, filename);
      toast.success('Reporte exportado correctamente');
    } catch (e) {
      console.error('Error exporting income/expense report:', e);
      toast.error('Error al exportar el reporte');
    } finally {
      setExporting(false);
    }
  }, [reportRepo, params, toast]);

  const setYear = useCallback((year: number) => {
    setParams(prev => ({ ...prev, year }));
  }, []);

  const setCompareYear = useCallback((compareYear: number | undefined) => {
    setParams(prev => ({ ...prev, compareYear }));
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    report,
    loading,
    exporting,
    params,
    setParams,
    setYear,
    setCompareYear,
    refetch: fetchReport,
    exportReport,
    currentYear,
  };
};
